import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { classificationSchemesQueryOptions } from '@/api/loans-api'
import type { ClassificationBand, LoanCycleDetail } from '@/api/loans-types'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { groupQueryOptions } from '@/features/groups/queries'
import { formatMoney, type Pesewas } from '@/lib/money'

import { formatRate } from './constants'
import { CountersignedOutcome, DualControlNotice } from './dual-control'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'
import { LoanLink } from './loan-link'
import { loanPaths, useLoanNavigate } from './loan-paths'
import {
  AmountField,
  DateField,
  FormField,
  NoteField,
  NumberField,
  OperationForm,
} from './operation-form'
import type { CycleOperationContext } from './operation-screen'
import {
  useExecuteGuaranteeOffset,
  useOverrideClassification,
  useRestructureRound,
  useWaivePenalty,
  useWriteOffRound,
} from './mutations'

/**
 * The workout screens: forgiving a charge, rescheduling a debt, giving one up, taking somebody
 * else's collateral for it, and classifying it by hand.
 *
 * All five are countersigned, and all five answer with the round rather than with a
 * `DualControlResult`. When the branch's approval policy defers one, the round comes back
 * unchanged and there is nothing on the response that says "queued" — so each of these compares
 * the one figure its action moves, before and after, and reports what that comparison can
 * honestly establish. See `dual-control.tsx`.
 */

/** The figure each action moves, so the screen can tell "applied" from "queued". */
const penaltyOf = (cycle: LoanCycleDetail) => cycle.summary.penaltyOutstandingMinor

export function WaivePenaltyForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const waive = useWaivePenalty(cycleId, accountId)

  const accrued = penaltyOf(cycle)
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [reason, setReason] = useState('')
  const [applied, setApplied] = useState<boolean | null>(null)

  const currency = cycle.summary.currencyCode
  const trimmedReason = reason.trim()
  const valid = amount !== null && amount > 0 && amount <= accrued && trimmedReason !== ''

  return (
    <OperationForm
      title="Waive accrued penalty"
      description="Reduces what the member owes. Nothing is posted — accrued penalty was never recognised as income."
      submitLabel="Waive penalty"
      tone="danger"
      isPending={waive.isPending}
      disabled={!valid}
      error={waive.isError ? waive.error : null}
      onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
      notice={
        <DualControlNotice
          action="Waiving penalty"
          approvePermission={LOAN_PERMISSIONS.penaltyWaiveApprove}
          canCountersign={permissions.canCountersignWaiver}
        />
      }
      outcome={
        applied === null ? null : (
          <CountersignedOutcome
            applied={applied}
            approvePermission={LOAN_PERMISSIONS.penaltyWaiveApprove}
            appliedDescription="The penalty outstanding on the round has come down by the amount waived."
            unchangedDescription="The penalty outstanding on the round is exactly what it was before you pressed the button."
          />
        )
      }
      onSubmit={() => {
        if (!valid) {
          return
        }

        waive.mutate(
          { amountMinor: amount, reason: trimmedReason },
          { onSuccess: (after) => setApplied(penaltyOf(after) < accrued) },
        )
      }}
      footnote="More than has accrued is refused outright — loans.penalty_waiver_exceeds_accrued."
    >
      {accrued === 0 ? (
        <Alert tone="info">
          <AlertTitle>No penalty has accrued</AlertTitle>
          <AlertDescription>
            There is nothing to waive on this round. The ageing run is what accrues penalty, and it
            has charged none here.
          </AlertDescription>
        </Alert>
      ) : null}

      <AmountField
        id="waive-amount"
        label="Amount to waive"
        currency={currency}
        value={amount}
        onChange={setAmount}
        autoFocus
        hint={
          <>
            The ceiling is the penalty accrued: {formatMoney(accrued, { currency })}. Key the
            amount being forgiven, not the balance.
          </>
        }
      />

      {amount !== null && amount > accrued ? (
        <Alert tone="danger">
          <AlertTitle>More than has accrued</AlertTitle>
          <AlertDescription>
            The round has{' '}
            <MoneyDisplay value={accrued} currency={currency} className="inline" /> of accrued
            penalty. Anything above that is refused.
          </AlertDescription>
        </Alert>
      ) : null}

      <NoteField
        id="waive-reason"
        label="Reason"
        required
        multiline
        value={reason}
        onChange={setReason}
        placeholder="Why the member should not have been charged this."
        hint="Read by whoever countersigns, and by whoever audits the waiver afterwards."
      />
    </OperationForm>
  )
}

/**
 * Rescheduling what is left.
 *
 * Posts nothing: a restructure moves due dates and rebuilds the schedule, it does not move money.
 * The old instalments are marked superseded rather than deleted, so the original agreement is
 * still readable afterwards.
 */
export function RestructureForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const restructure = useRestructureRound(cycleId, accountId)

  const [instalments, setInstalments] = useState('')
  const [firstRepaymentDate, setFirstRepaymentDate] = useState('')
  const [reason, setReason] = useState('')
  const [applied, setApplied] = useState<boolean | null>(null)

  const currency = cycle.summary.currencyCode
  const instalmentCount = Number.parseInt(instalments, 10)
  const trimmedReason = reason.trim()
  const valid =
    Number.isInteger(instalmentCount) &&
    instalmentCount > 0 &&
    firstRepaymentDate !== '' &&
    trimmedReason !== ''

  const before = {
    restructured: cycle.summary.isRestructured,
    maturity: cycle.summary.maturityDate,
  }

  return (
    <OperationForm
      title="Restructure the round"
      description="Reschedules the unpaid balance over a new set of instalments."
      submitLabel="Restructure"
      tone="danger"
      isPending={restructure.isPending}
      disabled={!valid}
      error={restructure.isError ? restructure.error : null}
      onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
      notice={
        <DualControlNotice
          action="Restructuring a round"
          approvePermission={LOAN_PERMISSIONS.restructureApprove}
          canCountersign={permissions.canCountersignRestructure}
        />
      }
      outcome={
        applied === null ? null : (
          <CountersignedOutcome
            applied={applied}
            approvePermission={LOAN_PERMISSIONS.restructureApprove}
            appliedDescription="The schedule has been rebuilt. The old instalments are marked superseded, and the round is flagged as restructured for the credit bureau extract."
            unchangedDescription="The schedule and the maturity date are exactly what they were before you pressed the button."
          />
        )
      }
      onSubmit={() => {
        if (!valid) {
          return
        }

        restructure.mutate(
          { instalmentCount, firstRepaymentDate, reason: trimmedReason },
          {
            onSuccess: (after) =>
              setApplied(
                after.summary.isRestructured !== before.restructured ||
                  after.summary.maturityDate !== before.maturity,
              ),
          },
        )
      }}
      footnote="A restructured facility is held at the scheme's floor class for a cooling period before it may be reported as performing again."
    >
      <Alert tone="warning">
        <AlertTitle>
          Rescheduling{' '}
          <MoneyDisplay
            value={cycle.summary.totalOutstandingMinor}
            currency={currency}
            className="inline"
          />
        </AlertTitle>
        <AlertDescription>
          That is the whole outstanding balance — principal, interest, fees and penalty. Nothing is
          written off and nothing is posted; only the dates and the instalment count change.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField
          id="restructure-instalments"
          label="New instalment count"
          value={instalments}
          onChange={setInstalments}
          min={1}
          hint={`The round was written over ${cycle.terms.instalmentCount}.`}
        />

        <DateField
          id="restructure-first-repayment"
          label="First repayment"
          value={firstRepaymentDate}
          onChange={setFirstRepaymentDate}
          hint="The rebuilt schedule runs from here, at the round's existing frequency."
        />
      </div>

      <NoteField
        id="restructure-reason"
        label="Reason"
        required
        multiline
        value={reason}
        onChange={setReason}
        placeholder="What changed for the member, and why this schedule is collectable when the last one was not."
      />
    </OperationForm>
  )
}

/** Giving the debt up, against the allowance. A governance act before it is an accounting one. */
export function WriteOffForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const writeOff = useWriteOffRound(cycleId, accountId)

  const [boardReference, setBoardReference] = useState('')
  const [reason, setReason] = useState('')
  const [applied, setApplied] = useState<boolean | null>(null)

  const currency = cycle.summary.currencyCode
  const trimmedReference = boardReference.trim()
  const trimmedReason = reason.trim()
  const valid = trimmedReference !== '' && trimmedReason !== ''
  const writtenOffBefore = cycle.writtenOffAmountMinor

  return (
    <OperationForm
      title="Write the round off"
      description="Against the allowance, with the board decision that authorised it."
      submitLabel="Write off"
      tone="danger"
      isPending={writeOff.isPending}
      disabled={!valid}
      error={writeOff.isError ? writeOff.error : null}
      onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
      notice={
        <DualControlNotice
          action="Writing a round off"
          approvePermission={LOAN_PERMISSIONS.writeOffApprove}
          canCountersign={permissions.canCountersignWriteOff}
        />
      }
      outcome={
        applied === null ? null : (
          <CountersignedOutcome
            applied={applied}
            approvePermission={LOAN_PERMISSIONS.writeOffApprove}
            appliedDescription="The round is written off and the journal is posted. Cash collected afterwards is recorded as a recovery; the round stays written off."
            unchangedDescription="The round is still live and nothing has been written off."
          />
        )
      }
      onSubmit={() => {
        if (!valid) {
          return
        }

        writeOff.mutate(
          { boardApprovalReference: trimmedReference, reason: trimmedReason },
          {
            onSuccess: (after) =>
              setApplied(
                after.writtenOffAmountMinor > writtenOffBefore ||
                  after.summary.status === 'WrittenOff',
              ),
          },
        )
      }}
      footnote="Recoveries afterwards are recorded against this round and do not revive it."
    >
      <Alert tone="danger">
        <AlertTitle>
          Giving up{' '}
          <MoneyDisplay
            value={cycle.summary.totalOutstandingMinor}
            currency={currency}
            className="inline"
          />
        </AlertTitle>
        <AlertDescription>
          The member still owes the money in law; the institution stops carrying it as an asset.
          {cycle.summary.amountInArrearsMinor > 0 ? (
            <>
              {' '}
              This round is {cycle.summary.daysInArrears} days behind on{' '}
              {formatMoney(cycle.summary.amountInArrearsMinor, { currency })}.
            </>
          ) : null}
        </AlertDescription>
      </Alert>

      <NoteField
        id="write-off-board-reference"
        label="Board approval reference"
        required
        autoFocus
        value={boardReference}
        onChange={setBoardReference}
        placeholder="BD/2026/041"
        hint="Mandatory. A write-off is a board decision before it is a ledger entry."
      />

      <NoteField
        id="write-off-reason"
        label="Reason"
        required
        multiline
        value={reason}
        onChange={setReason}
        placeholder="Why this debt is judged uncollectable, and what was attempted."
      />
    </OperationForm>
  )
}

/**
 * The group guarantee, executed.
 *
 * This is the single most consequential action in the area: one member's savings taken to settle
 * another member's debt. The signed agreement reference is mandatory, and the source is chosen
 * from the group's own members rather than keyed, so a customer id cannot be mistyped into
 * somebody else's deposit.
 */
export function GuaranteeOffsetForm({ cycle, account, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const offset = useExecuteGuaranteeOffset(cycleId, accountId)

  const group = useQuery({
    ...groupQueryOptions(account?.groupId ?? ''),
    enabled: Boolean(account?.groupId),
  })

  // The account arrives a moment after this screen does, so the borrower cannot be the initial
  // value of the select — it is folded in on first sight instead, and never over a choice the
  // operator has already made.
  const [chosenSource, setChosenSource] = useState('')
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [agreementReference, setAgreementReference] = useState('')
  const [reason, setReason] = useState('')
  const [applied, setApplied] = useState<boolean | null>(null)

  const currency = cycle.summary.currencyCode
  const sourceCustomerId = chosenSource === '' ? (account?.customerId ?? '') : chosenSource
  const trimmedAgreement = agreementReference.trim()
  const trimmedReason = reason.trim()
  const valid =
    sourceCustomerId !== '' &&
    amount !== null &&
    amount > 0 &&
    valueDate !== '' &&
    trimmedAgreement !== '' &&
    trimmedReason !== ''

  const guaranteesBefore = cycle.guarantees.length

  const members = (group.data?.members ?? []).filter((member) => member.status === 'Active')

  return (
    <OperationForm
      title="Execute the group guarantee"
      description="Takes a member's security deposit against this round's arrears."
      submitLabel="Execute the offset"
      tone="danger"
      isPending={offset.isPending}
      disabled={!valid}
      error={offset.isError ? offset.error : null}
      onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
      notice={
        <DualControlNotice
          action="Executing the guarantee"
          approvePermission={LOAN_PERMISSIONS.guaranteeOffsetApprove}
          canCountersign={permissions.canCountersignOffset}
        />
      }
      outcome={
        applied === null ? null : (
          <CountersignedOutcome
            applied={applied}
            approvePermission={LOAN_PERMISSIONS.guaranteeOffsetApprove}
            appliedDescription="The deposit has been taken and the offset is recorded on the round. Tell the member whose deposit it was — they will find out at their next statement otherwise."
            unchangedDescription="No offset has been recorded on this round and no deposit has been touched."
          />
        )
      }
      onSubmit={() => {
        if (!valid) {
          return
        }

        offset.mutate(
          {
            sourceCustomerId,
            amountMinor: amount,
            valueDate,
            agreementReference: trimmedAgreement,
            reason: trimmedReason,
          },
          { onSuccess: (after) => setApplied(after.guarantees.length > guaranteesBefore) },
        )
      }}
      footnote="The round must be in arrears — loans.offset_not_in_arrears — and the source must be an active member of the borrowing group."
    >
      <Alert tone="danger">
        <AlertTitle>This takes one member&apos;s money for another&apos;s debt</AlertTitle>
        <AlertDescription>
          It is what the signed group guarantee provides for, and it is the reason the guarantee is
          signed. The round is{' '}
          {cycle.summary.daysInArrears === 0
            ? 'not currently in arrears, and the server will refuse an offset against it'
            : `${cycle.summary.daysInArrears} days behind on ${formatMoney(cycle.summary.amountInArrearsMinor, { currency })}`}
          .
        </AlertDescription>
      </Alert>

      <FormField
        id="offset-source"
        label="Whose deposit"
        required
        hint={
          account?.groupId
            ? 'Active members of the borrowing group, plus the borrower themselves.'
            : 'This account has no group, so only the borrower’s own deposit can be taken.'
        }
      >
        {group.isPending && account?.groupId ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <Select value={sourceCustomerId} onValueChange={setChosenSource}>
            <SelectTrigger id="offset-source">
              <SelectValue placeholder="Choose a member" />
            </SelectTrigger>
            <SelectContent>
              {account ? (
                <SelectItem value={account.customerId}>
                  {account.customerName} — the borrower
                </SelectItem>
              ) : null}
              {members
                .filter((member) => member.customerId !== account?.customerId)
                .map((member) => (
                  <SelectItem key={member.customerId} value={member.customerId}>
                    {member.fullName} — {member.position.toLowerCase()}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </FormField>

      {account?.groupId && group.isError ? (
        <Alert tone="warning">
          <AlertTitle>The group&apos;s members could not be read</AlertTitle>
          <AlertDescription>
            Only the borrower is offered above. Reading a group needs{' '}
            <span className="font-mono text-xs">groups.view</span>; without it this screen cannot
            name the other members, and guessing at a customer id is not something it will do.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <AmountField
          id="offset-amount"
          label="Amount to take"
          currency={currency}
          value={amount}
          onChange={setAmount}
          hint={
            <>
              No more than the round owes:{' '}
              {formatMoney(cycle.summary.totalOutstandingMinor, { currency })}. The deposit itself
              is checked by the Savings module.
            </>
          }
        />

        <DateField
          id="offset-value-date"
          label="Value date"
          value={valueDate}
          onChange={setValueDate}
          hint="The date the deposit is taken and the round is credited."
        />
      </div>

      <NoteField
        id="offset-agreement"
        label="Signed agreement reference"
        required
        value={agreementReference}
        onChange={setAgreementReference}
        placeholder="GG/2026/118"
        hint="The guarantee the members actually signed. The server refuses an empty one."
      />

      <NoteField
        id="offset-reason"
        label="Reason"
        required
        multiline
        value={reason}
        onChange={setReason}
        placeholder="What was attempted with the borrower before the guarantee was called on."
      />
    </OperationForm>
  )
}

/**
 * Setting a facility's class by hand.
 *
 * The classes offered are the bands of the schemes actually in force — nothing here invents a
 * class code, because a code the scheme does not know is refused
 * (`loans.classification_override_class_unknown`) and would be a provisioning rate nobody chose.
 */
export function ClassificationOverrideForm({ cycle, accountId, cycleId }: CycleOperationContext) {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const override = useOverrideClassification(cycleId, accountId)
  const schemes = useQuery(classificationSchemesQueryOptions())

  const [classCode, setClassCode] = useState(cycle.overrideClassCode ?? '')
  const [reason, setReason] = useState('')
  const [applied, setApplied] = useState<boolean | null>(null)

  const trimmedReason = reason.trim()
  const valid = classCode !== '' && trimmedReason !== ''

  const bands = new Map<string, ClassificationBand>()
  for (const scheme of schemes.data ?? []) {
    for (const band of scheme.bands) {
      if (!bands.has(band.classCode)) {
        bands.set(band.classCode, band)
      }
    }
  }
  const chosen = classCode === '' ? undefined : bands.get(classCode)

  return (
    <OperationForm
      title="Override the classification"
      description="Sets the class by hand, over the one the run computed."
      submitLabel="Override the class"
      isPending={override.isPending}
      disabled={!valid}
      error={override.isError ? override.error : null}
      onDone={() => navigate(loanPaths.cycle(accountId, cycleId))}
      notice={
        <DualControlNotice
          action="Overriding a classification"
          approvePermission={LOAN_PERMISSIONS.classificationOverrideApprove}
          canCountersign={permissions.canCountersignOverride}
        />
      }
      outcome={
        applied === null ? null : (
          <CountersignedOutcome
            applied={applied}
            approvePermission={LOAN_PERMISSIONS.classificationOverrideApprove}
            appliedDescription="The round now carries the class you set, marked as an override. It is cleared automatically if the facility worsens past it."
            unchangedDescription="The round carries the class it carried before — no override has been recorded on it."
          />
        )
      }
      onSubmit={() => {
        if (!valid) {
          return
        }

        override.mutate(
          { classCode, reason: trimmedReason },
          {
            // The class alone is not enough to tell "applied" from "queued": the picker is
            // seeded with the override already in force, so re-pinning the same class with a new
            // reason would match on the class whether or not the request took effect. The reason
            // is what moved in that case, so both are compared.
            onSuccess: (after) =>
              setApplied(
                after.overrideClassCode === classCode && after.overrideReason === trimmedReason,
              ),
          },
        )
      }}
      footnote="An override is cleared if the facility deteriorates past the class it was pinned at. It can make a loan look worse than the run says, never quietly better for long."
    >
      {schemes.isPending ? (
        <Skeleton className="h-9 w-full" />
      ) : bands.size === 0 ? (
        <Alert tone="warning">
          <AlertTitle>No classification scheme is in force</AlertTitle>
          <AlertDescription>
            There are no bands to choose from, so nothing can be overridden. A scheme has to be put
            in force first —{' '}
            <LoanLink
              to={loanPaths.portfolio}
              className="font-medium text-brand-text underline underline-offset-2"
            >
              the portfolio screen
            </LoanLink>{' '}
            is where the schemes live.
          </AlertDescription>
        </Alert>
      ) : (
        <FormField
          id="override-class"
          label="Class"
          required
          hint="The bands of the schemes on file. The server refuses a code no scheme knows."
        >
          <Select value={classCode} onValueChange={setClassCode}>
            <SelectTrigger id="override-class">
              <SelectValue placeholder="Choose a class" />
            </SelectTrigger>
            <SelectContent>
              {[...bands.values()].map((band) => (
                <SelectItem key={band.classCode} value={band.classCode}>
                  {band.classCode} — {band.className}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      )}

      {chosen ? (
        <Alert tone="info">
          <AlertTitle>
            {chosen.className} provisions at {formatRate(chosen.provisionRate)}
          </AlertTitle>
          <AlertDescription>
            {chosen.isPerforming ? 'Treated as performing. ' : 'Treated as non-performing. '}
            {chosen.suspendsInterestAccrual
              ? 'Interest accrual is suspended at this class.'
              : 'Interest continues to accrue at this class.'}{' '}
            The round is currently{' '}
            {cycle.summary.classificationCode === null
              ? 'unclassified — no run has reached it'
              : cycle.summary.classificationCode}
            .
          </AlertDescription>
        </Alert>
      ) : null}

      <NoteField
        id="override-reason"
        label="Reason"
        required
        multiline
        value={reason}
        onChange={setReason}
        placeholder="Why this facility is classified differently from what the arrears alone say."
      />
    </OperationForm>
  )
}
